import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Colors from '../../../../ui/global/Colors';
import Fonts from '../../../../ui/global/Fonts';
import MenuPanel from '../../../../ui/components/MenuPanel';
import ArtMenu from './ArtMenu';
import SoundMenu from './SoundMenu';
import { useGameArt, GameArt } from '../../../../backend/useGameArt';
import { addArtToGame, createOrUpdateAssetsDuringEdit } from '../../../../ai/GameArtAI';
import { AssetSource } from '../../../../backend/useGameArt';
import { checkTransparencyAndRemoveBG } from '../../utils/checkTransparencyAndRemoveBG';
import { autoGenerateGameArt } from '../../../../ai/GameArtImageAI';
import TabPanel from '../../../../ui/components/TabPanel';
import ArtInner from './ArtInner';
import SoundInner from './SoundInner';
import { useArt } from '../../../../backend/useArt';
import { useGame } from '../../../../backend/useGame';
import { useStreamContext } from '../stream/helpers/StreamContext';
import { useStreamUIState } from '../stream/helpers/streamStore';
import { useMediaQuery } from '@mui/material';
import { useSharedPanelState } from '../panels/SharedPanelState';

const TabContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    margin: 0 auto;
`;

const Title = styled.h4`
    width: 100%;
    text-align: center;
    font-family: ${Fonts.lexendSemiBold.fontFamily};
    font-weight: ${Fonts.lexendSemiBold.fontWeight};
    color: ${Colors.grey1};
    padding: 0 12px;
    margin: 5px auto;
    z-index: 10;
`;

interface GameAssetsPanelProps {
    config: any;
    gameId: string;
    historyIteration: number;
    historyId: string;
    currentGameHistoryId: string;
    updatePlanStatus: (newStatus: string, newStatusType?: string, copyFromUserFacing?: boolean) => void;
    setIsUpdatingGameArt: (newStatus: boolean) => void;
    isGeneratingGame: boolean;
    isUpdatingGameArt: boolean;
    triggerReload: number;
    mode: string;
    isViewingLatest?: boolean;
    snapshotGameArt?: GameArt[];
    realtimeArtEvent?: { row: any; type: 'INSERT'|'UPDATE'|'DELETE' };
}

const GameAssetsPanel: React.FC<GameAssetsPanelProps> = ({ 
    config, 
    gameId, 
    historyIteration, 
    historyId,
    currentGameHistoryId, 
    updatePlanStatus, 
    setIsUpdatingGameArt, 
    isGeneratingGame, 
    isUpdatingGameArt,
    triggerReload,
    mode,
    isViewingLatest = true,
    snapshotGameArt,
    realtimeArtEvent,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { isAssetsPanelOpen: isOpen, toggleAssetsPanel: setIsOpen, isSettingsPanelOpen } = useSharedPanelState();
  const [artMenuOpen, setArtMenuOpen] = useState(false);
  const [soundMenuOpen, setSoundMenuOpen] = useState(false);
  const [selectedArtIndex, setSelectedArtIndex] = useState<number | null>(null);
  const [selectedSoundIndex, setSelectedSoundIndex] = useState<number | null>(null);
  const [gameArtAssets, setGameArtAssets] = useState<GameArt[]>([]);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const didInitialLiveFetchRef = useRef<boolean>(false);
  const lastLiveGameIdRef = useRef<string | null>(null);
  const lastIsViewingLatestRef = useRef<boolean | null>(null);
  const { listGameArt, associateGameArt, restoreGameArtToSnapshot, getGameArtById, getGameArtByName } = useGameArt();
  const { createArt, createHistoryEntry: createArtHistory, getArt } = useArt();
  const [loading, setLoading] = useState(true);
  const [updatingArtIds, setUpdatingArtIds] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState<'Art' | 'Sound'>('Art');
  const [inProgressGameHistoryId, setInProgressGameHistoryId] = useState<string | null>(null);
  const { streamContext } = useStreamContext();
  const streamUI = useStreamUIState();
  const fallbackRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Snapshot of prior asset_url values by game_art id to detect meaningful changes
  const prevAssetUrlByIdRef = useRef<Record<string, string>>({});

  // Reset baseline when game or view mode changes to avoid first-load false positives
  useEffect(() => {
    prevAssetUrlByIdRef.current = {};
  }, [gameId, isViewingLatest]);

  const upsertOrRemoveByRow = (updatedRow: GameArt | null, key?: { id?: string; name?: string }) => {
    setGameArtAssets(prev => {
      const next = Array.isArray(prev) ? [...prev] : [];
      if (updatedRow) {
        const idx = next.findIndex(a => a.id === updatedRow.id);
        if (idx >= 0) {
          next[idx] = { ...next[idx], ...updatedRow } as GameArt;
          return next;
        }
        const insertAt = (() => {
          try {
            const created = new Date(updatedRow.created_at).getTime();
            for (let i = 0; i < next.length; i++) {
              const t = new Date(next[i].created_at).getTime();
              if (created < t) return i;
            }
            return next.length;
          } catch {
            return next.length;
          }
        })();
        next.splice(insertAt, 0, updatedRow);
        return next;
      }
      if (key?.id) {
        const idx = next.findIndex(a => a.id === key.id);
        if (idx >= 0) next.splice(idx, 1);
        return next;
      }
      if (key?.name) {
        const idx = next.findIndex(a => String(a.asset_name || '').toLowerCase() === key.name!.toLowerCase());
        if (idx >= 0) next.splice(idx, 1);
        return next;
      }
      return next;
    });
  };

  // Track currently active agent art tool (single spinner model)
  const currentAgentArtToolRef = useRef<{ toolId: string; assetName?: string } | null>(null);
  // For create_art before the new asset appears in local list, resolve by name on realtime INSERT
  const pendingCreateByNameRef = useRef<string | null>(null);

  const WRITE_PLAN_TO_DB: boolean = true;
  const { savePlanToHistory } = useGame();

  const tabs = [
    { name: 'Art', icon: 'image' },
    { name: 'Sound', icon: 'sound' },
  ];

  // console.log("GameAssetsPanel log props", { 
  //   config, 
  //   gameId, 
  //   historyIteration, 
  //   currentGameHistoryId, 
  //   updatePlanStatus, 
  //   setIsUpdatingGameArt, 
  //   isGeneratingGame, 
  //   isUpdatingGameArt,
  //   triggerReload,
  //   mode,
  // });

  // useEffect(() => {
  //   console.log('game art assets:', gameArtAssets);
  // }, [gameArtAssets]);

  useEffect(() => {
    // console.log('history iteration:', historyIteration);
    if (historyIteration === 1) {
      setIsOpen(true);
    }
  }, [historyIteration, setIsOpen]);
  
  // Load game art depending on view mode
  useEffect(() => {
    const loadGameArt = async () => {
      if (!gameId) return;
      try {
        // Snapshot mode: set directly
        if (config.USE_NEW_GAME_ART_HISTORY && !isViewingLatest && snapshotGameArt && Array.isArray(snapshotGameArt)) {
          setLoading(true);
          setGameArtAssets(snapshotGameArt as GameArt[]);
          // Since we entered snapshot mode, ensure we fetch when returning to latest
          didInitialLiveFetchRef.current = false;
          return;
        }

        // New mode + latest: perform a single initial fetch per game id, then rely on realtime
        const needInitialLiveFetch = config.USE_NEW_GAME_ART_HISTORY && isViewingLatest && (!didInitialLiveFetchRef.current || lastLiveGameIdRef.current !== gameId);
        if (needInitialLiveFetch) {
          setLoading(true);
          const artAssets = await listGameArt(gameId);
          setGameArtAssets(artAssets);
          didInitialLiveFetchRef.current = true;
          lastLiveGameIdRef.current = gameId;
          return;
        }

        // Legacy mode or viewing latest with flag off: fetch on triggers
        if (!(config.USE_NEW_GAME_ART_HISTORY && isViewingLatest)) {
          setLoading(true);
          const artAssets = await listGameArt(gameId);
          setGameArtAssets(artAssets);
          return;
        }
        // New mode + latest and not initial: no-op (realtime will update in place)
      } catch (error) {
        console.error('Error loading game art:', error);
      } finally {
        setLoading(false);
      }
    };
    loadGameArt();
  }, [gameId, listGameArt, triggerReload, config.USE_NEW_GAME_ART_HISTORY, isViewingLatest, snapshotGameArt, historyId]);

  // Force a live fetch when transitioning from snapshot to latest to replace snapshot content
  useEffect(() => {
    const fetchOnTransition = async () => {
      if (!config.USE_NEW_GAME_ART_HISTORY) return;
      if (!gameId) return;
      const wasLatest = lastIsViewingLatestRef.current;
      if (wasLatest === false && isViewingLatest) {
        try {
          setLoading(true);
          const artAssets = await listGameArt(gameId);
          setGameArtAssets(artAssets);
          didInitialLiveFetchRef.current = true;
          lastLiveGameIdRef.current = gameId;
        } catch (error) {
          console.error('Error fetching game art on transition to latest:', error);
        } finally {
          setLoading(false);
        }
      }
      lastIsViewingLatestRef.current = isViewingLatest;
    };
    fetchOnTransition();
  }, [isViewingLatest, gameId, config.USE_NEW_GAME_ART_HISTORY, listGameArt]);

  // Apply realtime deltas in-place to avoid full reload and preserve scroll
  useEffect(() => {
    if (!config.USE_NEW_GAME_ART_HISTORY) return;
    if (!isViewingLatest) return; // realtime only on latest
    if (!realtimeArtEvent) return;

    const row = realtimeArtEvent.row as GameArt;
    if (realtimeArtEvent.type === 'DELETE') {
      upsertOrRemoveByRow(null, { id: row.id });
    } else {
      upsertOrRemoveByRow(row);
    }

    // Cancel pending fallback refresh since realtime arrived
    if (fallbackRefreshTimerRef.current) {
      clearTimeout(fallbackRefreshTimerRef.current);
      fallbackRefreshTimerRef.current = null;
    }
  }, [realtimeArtEvent, config.USE_NEW_GAME_ART_HISTORY, isViewingLatest]);

  // Auto-open panel on meaningful asset changes (url change/add/delete) when viewing latest
  useEffect(() => {
    if (!config.AUTO_OPEN_ASSETS_PANEL_ON_ASSET_CHANGE) return;
    if (isMobile && isSettingsPanelOpen) return;
    if (!isViewingLatest) return;

    const normalize = (v?: string | null) => (v ?? '').trim();
    const prev = prevAssetUrlByIdRef.current;
    const next: Record<string, string> = {};

    for (const a of gameArtAssets || []) {
      if (!a || !a.id) continue;
      next[a.id] = normalize(a.asset_url);
    }

    const hadBaseline = Object.keys(prev).length > 0;
    let changed = false;

    if (hadBaseline) {
      // Detect URL changes
      for (const id of Object.keys(next)) {
        const before = prev[id];
        const after = next[id];
        if (before !== undefined && before !== after) {
          const meaningfulBefore = before.length > 0;
          const meaningfulAfter = after.length > 0;
          if (meaningfulBefore !== meaningfulAfter || (meaningfulBefore && meaningfulAfter)) {
            changed = true;
            break;
          }
        }
      }
      // Detect added assets
      if (!changed) {
        for (const id of Object.keys(next)) {
          if (!(id in prev)) { changed = true; break; }
        }
      }
      // Detect deleted assets
      if (!changed) {
        for (const id of Object.keys(prev)) {
          if (!(id in next)) { changed = true; break; }
        }
      }
    }

    // Update baseline before acting to avoid re-entrancy issues
    prevAssetUrlByIdRef.current = next;

    if (changed && !isOpen) {
      setIsOpen(true);
    }
  }, [gameArtAssets, isViewingLatest, config.AUTO_OPEN_ASSETS_PANEL_ON_ASSET_CHANGE, isOpen, setIsOpen]);

  // Resolve pending create_art spinner once the asset with that name appears in list
  useEffect(() => {
    if (!config.USE_NEW_GAME_ART_HISTORY) return;
    if (!isViewingLatest) return;
    const pendingName = pendingCreateByNameRef.current;
    if (!pendingName) return;
    const matchIndex = gameArtAssets.findIndex(a => String(a.asset_name || '').toLowerCase() === String(pendingName).toLowerCase());
    if (matchIndex >= 0) {
      const id = gameArtAssets[matchIndex]?.id;
      if (id) {
        setUpdatingArtIds(prev => new Set(prev).add(id));
      }
      pendingCreateByNameRef.current = null;
    }
  }, [gameArtAssets, config.USE_NEW_GAME_ART_HISTORY, isViewingLatest]);

  // Listen to stream tool events to toggle spinner for create_art / update_art
  useEffect(() => {
    if (!config.USE_NEW_GAME_ART_HISTORY) return;
    if (!isViewingLatest) return;
    const blocks = (streamUI && streamUI.content && Array.isArray(streamUI.content.blocks)) ? streamUI.content.blocks : [];
    if (!blocks.length) return;

    // Find latest relevant art tool block
    let latestTool: any = null;
    for (let i = blocks.length - 1; i >= 0; i--) {
      const b: any = blocks[i];
      if (!b || b.type !== 'tool') continue;
      const t = b.content || {};
      const name = String(t.name || '');
      if (name === 'create_art' || name === 'update_art') {
        latestTool = t;
        break;
      }
    }
    if (!latestTool) return;

    const status = String(latestTool.status || '');
    const toolId = String(latestTool.id || '');
    const args = (latestTool.params && (latestTool.params.args || latestTool.params)) || {};
    const result = (latestTool.params && latestTool.params.result) || {};
    const assetName: string = String(args.asset_name || result.asset_name || '');
    const assetIdFromArgs: string | undefined = String(
      args.game_art_id || args.asset_id || args.gameArtId || args.assetId || ''
    ) || undefined;
    const assetIdFromResult: string | undefined = String(
      result?.game_art_id || result?.asset_id || result?.gameArtId || result?.assetId || ''
    ) || undefined;

    if (status === 'called') {
      currentAgentArtToolRef.current = { toolId, assetName };
      if (assetIdFromArgs) {
        setUpdatingArtIds(prev => new Set(prev).add(assetIdFromArgs));
      } else if (assetName) {
        const idx = gameArtAssets.findIndex(a => String(a.asset_name || '').toLowerCase() === assetName.toLowerCase());
        if (idx >= 0) {
          const id = gameArtAssets[idx]?.id;
          if (id) setUpdatingArtIds(prev => new Set(prev).add(id));
        } else {
          pendingCreateByNameRef.current = assetName;
        }
      }
      // Cancel any prior fallback refresh for a previous tool
      if (fallbackRefreshTimerRef.current) {
        clearTimeout(fallbackRefreshTimerRef.current);
        fallbackRefreshTimerRef.current = null;
      }
    } else if (status === 'complete' || status === 'error') {
      // Prefer clearing by id when available; otherwise fall back to name
      if (assetIdFromResult) {
        setUpdatingArtIds(prev => { const n = new Set(prev); n.delete(assetIdFromResult); return n; });
      } else if (assetName) {
        const idx = gameArtAssets.findIndex(a => String(a.asset_name || '').toLowerCase() === assetName.toLowerCase());
        if (idx >= 0) {
          const id = gameArtAssets[idx]?.id;
          if (id) setUpdatingArtIds(prev => { const n = new Set(prev); n.delete(id); return n; });
        }
      }
      if (currentAgentArtToolRef.current && currentAgentArtToolRef.current.toolId === toolId) {
        pendingCreateByNameRef.current = null;
        currentAgentArtToolRef.current = null;
      }

      // Fallback refresh: if realtime does not arrive shortly, refresh list
      const performTargetedRefresh = async () => {
        try {
          if (!gameId) return;
          // Prefer id-based update; fallback to name-based
          const idOrName = assetIdFromResult || assetName;
          if (!idOrName) return;

          let updatedRow: GameArt | null = null;
          if (assetIdFromResult) {
            updatedRow = await getGameArtById(assetIdFromResult);
          } else if (assetName) {
            updatedRow = await getGameArtByName(gameId, assetName);
          }

          setGameArtAssets(prev => {
            const next = Array.isArray(prev) ? [...prev] : [];
            // If we have an updated row, update/insert; otherwise, remove if it exists (e.g., deleted by rollback)
            if (updatedRow) {
              const idx = next.findIndex(a => a.id === updatedRow!.id);
              if (idx >= 0) {
                next[idx] = { ...next[idx], ...updatedRow } as GameArt;
              } else {
                // Maintain created_at ordering
                const insertAt = (() => {
                  try {
                    const created = new Date(updatedRow!.created_at).getTime();
                    for (let i = 0; i < next.length; i++) {
                      const t = new Date(next[i].created_at).getTime();
                      if (created < t) return i;
                    }
                    return next.length;
                  } catch {
                    return next.length;
                  }
                })();
                next.splice(insertAt, 0, updatedRow as GameArt);
              }
            } else {
              // Row no longer valid/visible; remove by id or by name
              if (assetIdFromResult) {
                const idx = next.findIndex(a => a.id === assetIdFromResult);
                if (idx >= 0) next.splice(idx, 1);
              } else if (assetName) {
                const idx = next.findIndex(a => String(a.asset_name || '').toLowerCase() === assetName.toLowerCase());
                if (idx >= 0) next.splice(idx, 1);
              }
            }
            return next;
          });
        } catch (err) {
          console.error('Error performing targeted art refresh:', err);
        }
      };

      if (!config.USE_NEW_GAME_ART_HISTORY) {
        // If realtime disabled, perform targeted fetch immediately
        performTargetedRefresh();
      } else {
        // Debounced targeted fetch if no realtime event within 1s
        if (fallbackRefreshTimerRef.current) {
          clearTimeout(fallbackRefreshTimerRef.current);
        }
        fallbackRefreshTimerRef.current = setTimeout(() => {
          performTargetedRefresh().finally(() => { fallbackRefreshTimerRef.current = null; });
        }, 1000);
      }
    }
  }, [streamUI?.content?.blocks, config.USE_NEW_GAME_ART_HISTORY, isViewingLatest, gameArtAssets, getGameArtById, getGameArtByName, gameId]);
  
  useEffect(() => {
    setIsUpdatingGameArt(updatingArtIds.size > 0);
  }, [updatingArtIds, setIsUpdatingGameArt]);
  
  const handleArtClick = (artIndex: number) => {
    // Open the ArtMenu and set the selected art index
    setSelectedArtIndex(artIndex);
    setArtMenuOpen(true);
    setSoundMenuOpen(false); // Ensure sound menu is closed when art menu opens
    // console.log('Clicked art at index:', artIndex);
  };

  const handleSoundClick = (soundIndex: number) => {
    // Open the SoundMenu and set the selected sound index
    setSelectedSoundIndex(soundIndex);
    setSoundMenuOpen(true);
    setArtMenuOpen(false); // Ensure art menu is closed when sound menu opens
    // console.log('Clicked sound at index:', soundIndex);
  };

  const handleSelectReplacementArt = async (artId: string, aiImage: string, artName: string) => {
    // console.log(`Selected art ID ${artId} to replace art at index ${selectedArtIndex}`);
    const name = gameArtAssets[selectedArtIndex!].asset_name || artName;
    
    // Save to database with explicit asset source
    await addImageToGame(name, aiImage, artId, 'selected_art');
    handleArtMenuClose(false);
  };

  const handleArtUploaded = async (image: string, assetSource: AssetSource = 'user_upload') => {
    // console.log('Art uploaded:', image, 'for selected art at index:', selectedArtIndex);

    if (selectedArtIndex === null || !gameArtAssets[selectedArtIndex]) {
      console.error('No art selected for upload');
      return;
    }

    const name = gameArtAssets[selectedArtIndex].asset_name;
    await addImageToGame(name, image, undefined, assetSource);
  };

  const addImageToGame = async (name: string, asset: string, artId: string | null, assetSource: AssetSource) => {
    const id = gameArtAssets[selectedArtIndex!].id;
    // console.log('Adding art to game:', gameId, 'for art id:', artId, 'with game art id:', id);
    
    try {
        // If viewing a non-latest history entry, restore game_art to the snapshot before applying updates
        try {
            if (config.USE_NEW_GAME_ART_HISTORY && !isViewingLatest && snapshotGameArt && Array.isArray(snapshotGameArt) && gameId) {
                const snapshotForRestore = (snapshotGameArt as any[]).map(s => ({
                  id: s.id,
                  game_id: s.game_id,
                  art_id: s.art_id,
                  asset_url: s.asset_url,
                  asset_name: s.asset_name,
                  asset_source: s.asset_source,
                }));
                await restoreGameArtToSnapshot(gameId, snapshotForRestore);
            }
        } catch (restoreErr) {
            console.error('Error restoring game art snapshot before add:', restoreErr);
            return;
        }
        // Check if we have a valid AI image to add
        if (!asset) {
            console.error('No AI image available to add to game');
            return;
        }
        
        // Set the updating state for the selected art asset
        if (id) {
          setUpdatingArtIds(prev => new Set(prev).add(id));
        }
        // Only mark as "Request Started" for manual uploads or selected existing art
        let lastHistoryId: string | null = null;
        if (assetSource === 'user_upload' || assetSource === 'selected_art') {
          lastHistoryId = await writePlan(`Update art: ${name}`, "Request Started", false);
        }

        // Close the art menu
        setArtMenuOpen(false);
        
        // Store the original art asset before making changes
        const originalArtAsset = gameArtAssets[selectedArtIndex!];
        
        // Check transparency and remove background if needed
        const processedAsset = await checkTransparencyAndRemoveBG(asset, name, config, updatePlanStatus, writePlan);
        lastHistoryId = (await writePlan(`Update art: ${name}`, "Add Art to Game", false)) || lastHistoryId;

        let finalArtId = artId;
        let newArtHistoryEntry;

        if (assetSource === 'ai_generated' || assetSource === 'user_upload') {
            // For AI generated or uploaded art, always create new art and history
            const newArt = await createArt(name);
            finalArtId = newArt.id;

            // Create a new history entry for this new art
            newArtHistoryEntry = await createArtHistory(
                newArt.id,
                name, // description
                null, // userPng
                processedAsset, // aiImage
                [] // objects
            );
            // console.log('Successfully created new art and history entry:', newArtHistoryEntry);
        } else if (assetSource === 'selected_art') {
            // For selected art, only create new history if only background was removed
            if (!finalArtId) {
                console.error('No art ID provided for selected art');
                return;
            }

            // Get the art with its history entries
            const artWithHistory = await getArt(finalArtId);
            if (!artWithHistory || !artWithHistory.art_history_entries || artWithHistory.art_history_entries.length === 0) {
                console.error('No history found for selected art');
                return;
            }

            // Get the latest history entry
            const latestHistory = artWithHistory.art_history_entries[artWithHistory.art_history_entries.length - 1];

            // Create a new history entry that copies previous entry but updates the AI image
            newArtHistoryEntry = await createArtHistory(
                finalArtId,
                latestHistory.description, // Keep the previous description
                latestHistory.user_png, // Keep the same user PNG
                processedAsset, // Update only the AI image
                latestHistory.objects || [] // Keep the same objects
            );
            // console.log('Successfully created new history entry for selected art:', newArtHistoryEntry);
        }
        
        // Create a game art entry using the useGameArt hook
        const gameArt = await associateGameArt(
            gameId,
            name,
            processedAsset,
            assetSource,
            finalArtId,
            id // Pass the existing game art ID for updates
        );
        // console.log('Successfully added art to game in database:', gameArt);

        // Refresh the game art list from the database
        const updatedArtAssets = await listGameArt(gameId);
        setGameArtAssets(updatedArtAssets);

        try {
            const historyIdToStartFrom = lastHistoryId || inProgressGameHistoryId || historyId;
            // Update the game code to integrate the new art asset
            if (!historyIdToStartFrom) {
                console.error('No current game history ID');
                return;
            }
            // Clear previous stream content right before starting the new stream
            try {
              streamContext?.clearContent();
              streamContext?.setIsStreaming(true);
              streamContext?.setStreamPhase('Connecting');
            } catch (_) {}
            const integrateArt = await addArtToGame(gameId, [id], historyIdToStartFrom, `Update art: ${name}`, streamContext || undefined);
            // console.log('Successfully integrated art into game:', integrateArt);
        } catch (integrationError) {
            console.error('Error integrating art into game:', integrationError);
            
            // Revert back to the original art asset in the database
            // console.log('Reverting to original art asset:', originalArtAsset);
            
            try {
                // Restore the original art asset
                await associateGameArt(
                    gameId,
                    originalArtAsset.asset_name || name,
                    originalArtAsset.asset_url,
                    originalArtAsset.asset_source as AssetSource,
                    originalArtAsset.art_id,
                    id
                );
                
                // Refresh the game art list to show the reverted asset
                const revertedArtAssets = await listGameArt(gameId);
                setGameArtAssets(revertedArtAssets);
                
                // console.log('Successfully reverted to original art asset');
            } catch (revertError) {
                console.error('Error reverting to original art asset:', revertError);
            }
            // Finalize with error since integration failed
            try {
              await finalizeEditError(`Update art: ${name}`, lastHistoryId || inProgressGameHistoryId);
            } catch (_) {}
        }
    } catch (error) {
        console.error('Error adding art to game:', error);
        // Finalize with error if this request had started a plan
        try {
          await finalizeEditError(`Update art: ${name}`, null);
        } catch (_) {}
    } finally {
        // Clear the updating state
        if (id) {
          setUpdatingArtIds(prev => { const n = new Set(prev); n.delete(id); return n; });
        }
        setIsUpdatingGameArt(false);
        setInProgressGameHistoryId(null);
    }
  };

  const handleAutoGenerateArt = async () => {
    const BACKEND_AUTO_GENERATE_ART = false;
    // console.log('Auto-generate art triggered from GameArtPanel');
    
    if (selectedArtIndex === null || !gameArtAssets[selectedArtIndex]) {
      console.error('No art selected for auto-generation');
      return;
    }
    
    try {
      let autoGenHistoryId: string | null = null;
      // If viewing a non-latest history entry, restore game_art to the snapshot before applying updates
      try {
        if (config.USE_NEW_GAME_ART_HISTORY && !isViewingLatest && snapshotGameArt && Array.isArray(snapshotGameArt) && gameId) {
          const snapshotForRestore = (snapshotGameArt as any[]).map(s => ({
            id: s.id,
            game_id: s.game_id,
            art_id: s.art_id,
            asset_url: s.asset_url,
            asset_name: s.asset_name,
            asset_source: s.asset_source,
          }));
          await restoreGameArtToSnapshot(gameId, snapshotForRestore);
        }
      } catch (restoreErr) {
        console.error('Error restoring game art snapshot before auto-generate:', restoreErr);
        return;
      }
      // Set loading state
      const selectedArt = gameArtAssets[selectedArtIndex];
      const currentId = selectedArt.id;
      if (currentId) {
        setUpdatingArtIds(prev => new Set(prev).add(currentId));
      }
      autoGenHistoryId = await writePlan(`Update art: ${gameArtAssets[selectedArtIndex].asset_name}`, "Auto-Generating Art", false);
      
      // Close the art menu
      setArtMenuOpen(false);
      
      // Get the selected art details
      
      if (BACKEND_AUTO_GENERATE_ART) {
        // Backend generation path
        // console.log('Using backend generation path');
        // Clear previous stream content right before starting the new backend stream
        try {
          streamContext?.clearContent();
          streamContext?.setIsStreaming(true);
          streamContext?.setStreamPhase('Connecting');
        } catch (_) {}
        const result = await createOrUpdateAssetsDuringEdit(
          gameId,
          autoGenHistoryId || inProgressGameHistoryId,
          `Update art: ${selectedArt.asset_name}`
        );
        // console.log('Backend generation result:', result);

        // Refresh the game art list from the database
        const updatedArtAssets = await listGameArt(gameId);
        setGameArtAssets(updatedArtAssets);

        if (currentId) {
          setUpdatingArtIds(prev => { const n = new Set(prev); n.delete(currentId); return n; });
        }
        setIsUpdatingGameArt(false);
      } else {
        // Frontend generation path
        const generatedImage = await autoGenerateGameArt({
          req: {
            id: selectedArt.id,
            asset_name: selectedArt.asset_name,
            asset_description: selectedArt.asset_description,
          },
          config
        });
        
        if (!generatedImage) {
          console.error('Failed to generate art image');
          if (currentId) {
            setUpdatingArtIds(prev => { const n = new Set(prev); n.delete(currentId); return n; });
          }
          setIsUpdatingGameArt(false);
          // Finalize with error for failed generation
          try {
            await finalizeEditError(`Update art: ${selectedArt.asset_name}`, autoGenHistoryId || inProgressGameHistoryId);
          } catch (_) {}
          return;
        }
        
        // console.log('Successfully generated AI art', generatedImage);

        // Add the generated image to the game with explicit asset source
        await handleArtUploaded(generatedImage.url, 'ai_generated');
      }
      
    } catch (error) {
      console.error('Error auto-generating art:', error);
      // Finalize with error for auto-generate path failures
      try {
        const selectedArt = gameArtAssets[selectedArtIndex!];
        // Prefer clearing any existing in-progress DB entry to auto-show tooltip
        await finalizeEditError(`Update art: ${selectedArt?.asset_name}`, inProgressGameHistoryId);
      } catch (_) {}
      // Ensure spinner/UI cleanup on thrown errors too
      try {
        const currentId = gameArtAssets[selectedArtIndex!]?.id;
        if (currentId) {
          setUpdatingArtIds(prev => { const n = new Set(prev); n.delete(currentId); return n; });
        }
        setIsUpdatingGameArt(false);
      } catch (_) {}
    }
  };

  // Write a final error entry when an edit request that started a plan fails
  const finalizeEditError = async (prompt: string, startedHistoryId?: string | null) => {
    const idToUse = startedHistoryId || inProgressGameHistoryId;
    if (idToUse) {
      try {
        await savePlanToHistory(
          idToUse,
          gameId,
          prompt,
          'Edit Failed',
          'Add Game Art',
          'ERROR',
          true
        );
      } catch (e) {
        console.error('Error saving final error plan to history:', e);
      }
    } else {
      // No DB plan exists for this request; update plan status in the frontend only
      try {
        updatePlanStatus('Edit Failed', 'Add Game Art');
      } catch (_) {}
    }
  };

  const writePlan = async (prompt: string, newPlanStatus: string, userFacing: boolean) => {
    const newPlanStatusType = "Add Game Art";
    updatePlanStatus(newPlanStatus, newPlanStatusType);

    if (WRITE_PLAN_TO_DB) {
      const historyResult = await savePlanToHistory(historyId, gameId, prompt, newPlanStatus, newPlanStatusType, 'IN_PROGRESS', userFacing);
      if (historyResult && historyResult.id) {
        setInProgressGameHistoryId(historyResult.id);
        return historyResult.id;
      }
    }
    return null;
  };

  const handleArtMenuClose = (open: boolean) => {
    if (!open) {
      setSelectedArtIndex(null);
    }
    setArtMenuOpen(open);
  };

  const handleSoundMenuClose = (open: boolean) => {
    if (!open) {
      setSelectedSoundIndex(null);
    }
    setSoundMenuOpen(open);
  };

  const handleSelectReplacementSound = async (soundId: string, soundUrl: string, soundName: string) => {
    // Implementation for handling sound replacement would go here
    console.log(`Selected sound ID ${soundId} to replace sound at index ${selectedSoundIndex}`);
    handleSoundMenuClose(false);
  };

  const handleSoundUploaded = async (sound: string) => {
    console.log('Sound uploaded for selected sound at index:', selectedSoundIndex);
    handleSoundMenuClose(false);
  };

  const onTabClick = (tab: 'Art' | 'Sound') => {
    setArtMenuOpen(false);
    setSoundMenuOpen(false);
    setSelectedArtIndex(null);
    setSelectedSoundIndex(null);
    setTab(tab);
  };

  if (gameArtAssets.length === 0 || mode === 'Code') {
    return null;
  }

  return (
    <>
      <MenuPanel
          ref={menuPanelRef}
          open={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open);
            // If the panel is being closed, close both menus
            if (!open) {
              setArtMenuOpen(false);
              setSoundMenuOpen(false);
              setSelectedArtIndex(null);
              setSelectedSoundIndex(null);
            }
            // Don't open any menus when opening the panel
          }}
          position={'left'}
          width={160}
          colorMode="dark"
          expandTooltip={!isOpen ? "Game Assets" : null}
          title={
          config.GAME_ASSETS_INTEGRATION.sound ? (
            <TabContainer>
                <TabPanel
                    tabs={tabs}
                    onSelect={onTabClick}
                    defaultTabName='Art'
                    colorMode='dark'
                    size='tiny'
                />
            </TabContainer>
          ) : (
            <Title>Game Art</Title>
          )}
      >
          {tab === 'Art' ? (
            <ArtInner 
              gameArtAssets={gameArtAssets}
              loading={loading}
              selectedArtIndex={selectedArtIndex}
              updatingArtIds={updatingArtIds}
              onArtClick={handleArtClick}
            />
          ) : (
            <SoundInner 
              loading={false}
              onSoundClick={handleSoundClick}
              selectedSoundIndex={selectedSoundIndex}
            />
          )}
      </MenuPanel>
      
      {/* Art Menu for selecting replacement art */}
      {artMenuOpen && menuPanelRef.current && config.GAME_ASSETS_INTEGRATION.panel && (
        <ArtMenu 
            isOpen={artMenuOpen} 
            onOpenChange={(open) => {
              handleArtMenuClose(open);
              // Ensure sound menu stays closed when art menu changes
              if (open) {
                setSoundMenuOpen(false);
              }
            }}
            selectedArtIndex={selectedArtIndex}
            onArtSelected={handleSelectReplacementArt}
            onArtUploaded={handleArtUploaded}
            isGeneratingGame={isGeneratingGame}
            isUpdatingGameArt={isUpdatingGameArt}
            onAutoGenerateClick={handleAutoGenerateArt}
        />
      )}

      {/* Sound Menu for selecting replacement sound */}
      {soundMenuOpen && menuPanelRef.current && config.GAME_ASSETS_INTEGRATION.sound && (
        <SoundMenu 
            isOpen={soundMenuOpen} 
            onOpenChange={(open) => {
              handleSoundMenuClose(open);
              // Ensure art menu stays closed when sound menu changes
              if (open) {
                setArtMenuOpen(false);
              }
            }}
            selectedSoundIndex={selectedSoundIndex}
            onSoundSelected={handleSelectReplacementSound}
            onSoundUploaded={handleSoundUploaded}
            isGeneratingGame={isGeneratingGame}
            isUpdatingGameSound={isUpdatingGameArt}
        />
      )}
    </>
  );
};

export default GameAssetsPanel;